import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { BreadCrumb } from '@nationwide/internet-servicing-angular-components';
import { FormatPolicyNumberService } from 'app/commercial/claims/services/format-policy-number.service';
import { PackageService } from 'app/commercial/claims/services/package.service';
import { PolicyInfoService } from 'app/commercial/claims/services/policy-info.service';
import { CelebrusTrackingService } from 'app/shared/services/celebrus-tracking.service';
import { environment } from 'environments/environment';
import { Subscription } from 'rxjs';
import { commercialConstants } from '../../../../common/commercial-constants';
import { LoggerService } from '../../../../shared/services/logger.service';
import { ClaimsDataService } from '../../services/claims-data.service';
import { CommercialLoggingService } from '../../services/commercial-logging.service';
import { IconSvgService } from '../../services/icon-svg.service';
import { SearchForClaimsService } from '../../services/search-for-claims.service';
@Component({
    selector: 'is-policy-claims-list',
    templateUrl: './policy-claims-list.component.html',
    styleUrls: ['./policy-claims-list.component.scss'],
    standalone: false
})
export class PolicyClaimsListComponent implements OnInit, OnDestroy {
    accountNumber: any;
    breadCrumbs: BreadCrumb[] = [];
    claimsList: any[] = [];
    closedClaimsFilter = false;
    currentPage = 'Policy Claims List';
    // eslint-disable-next-line no-magic-numbers
    displayLimit = 5;
    displayOffset = 0;
    hasClaims = false;
    hasOpenClaims = false;
    homeIconLink = environment.NATIONWIDE.accountSummaryPage;
    isAuto = false;
    isLoadComplete = false;
    package: any;
    policy: any;
    policyNumber = '';
    policyType = '';
    previousDisplayOffset = 0;
    subscription = new Subscription();
    totalClaims = 0;

    constructor(
        private LOGGER: LoggerService,
        private router: Router,
        public claimsDataService: ClaimsDataService,
        public policyInfoService: PolicyInfoService,
        private packageService: PackageService,
        private searchClaimsService: SearchForClaimsService,
        private iconSvgService: IconSvgService,
        private formatPolicyNumberService: FormatPolicyNumberService,
        private celebrusService: CelebrusTrackingService,
        private commercialLoggingService: CommercialLoggingService
    ) { }
    checkClosedClaims(showClosed: boolean): void {
        this.searchClaimsService.retrieveOpenClosedClaims(showClosed, this.claimsDataService.getClaimsAccountNumber()).then((response) => {
            const numberOfClaims = response.claimSummaries.length;
            if (numberOfClaims > 0) {
                this.hasOpenClaims = true;
                this.processClaimsList(response.claimSummaries);
            } else {
                this.isLoadComplete = true;
                this.hasClaims = false;
            }
        });
    }
    ctmLogConfirmationEvent(): void {
        this.commercialLoggingService.logButtonAction(
            this.LOGGER,
            this.currentPage,
            'start-claim',
            'success',
            this.getLogMetadata()
        );
        this.commercialLoggingService.updatePreviousUrlContext();
    }
    fileAGlassClaim(): void {
        this.commercialLoggingService.logButtonAction(
            this.LOGGER,
            this.currentPage,
            'start-glass-claim',
            'success',
            this.getLogMetadata()
        );
        this.commercialLoggingService.updatePreviousUrlContext();
        window.open(environment.NATIONWIDE.GLASS_CLAIM, '_blank');
    }
    findPolicy(): any {
        const subStringStart = 4;
        const subStringEnd = 8;
        const prefixB = this.policyNumber.slice(subStringStart, subStringEnd);
        for (const policy of this.package.policies) {
            if (policy.prefixB === prefixB.trim()) {
                return policy;
            }
        }
        return null;
    }
    navigateToClaimOverview(claimNumber: string, premisisID: any): void {
        this.claimsDataService.setClaimNumber(claimNumber);
        this.claimsDataService.setFnolFlow(this.policy);
        this.claimsDataService.setClaimsPolicyNumber(this.policyNumber);
        this.celebrusService.storeProductType();
        this.claimsDataService.setClaimsPolicyType(this.policyInfoService.getDisplayName(this.policy));
        this.claimsDataService.setPremisisID(premisisID);
        this.commercialLoggingService.logButtonAction(
            this.LOGGER,
            this.currentPage,
            'navigate-claim-overview',
            'success',
            this.getLogMetadata({ claimNumber })
        );
        this.commercialLoggingService.updatePreviousUrlContext();
        this.router.navigate([commercialConstants.URLS.claimOverview]);
    }
    ngOnDestroy(): void {
        this.subscription.unsubscribe();
        this.celebrusService.clearCelebrusSessionStorage();
    }

    ngOnInit(): void {
        this.breadCrumbs = [{
            name: 'Package (PAK) Claims',
            link: `${commercialConstants.URLS.pakClaimsList}`
        }];
        this.policyType = this.claimsDataService.getClaimsPolicyType();
        this.policyNumber = this.claimsDataService.getClaimsPolicyNumber();
        const lookupIdentifier = this.claimsDataService.getClaimsLookupIdentifier();
        this.accountNumber = lookupIdentifier;
        this.isAuto = this.policyType === 'Business Auto';
        this.commercialLoggingService.logPageLoad(this.LOGGER, this.currentPage, this.getLogMetadata());

        this.packageService.retrievePackageDataforClaims(lookupIdentifier).subscribe((response) => {
            try {
                if (response && Object.keys(response).length > 0) {
                    this.package = response;
                    this.claimsDataService.hydratePackageContext(this.package);
                    this.accountNumber = this.claimsDataService.getClaimsAccountNumber();
                    this.policy = this.findPolicy();
                    if (this.policy) {
                        this.policyType = this.policyInfoService.getDisplayName(this.policy);
                        this.claimsDataService.setClaimsPolicyNumber(this.policyNumber);
                        this.claimsDataService.setClaimsPolicyType(this.policyType);
                        this.claimsDataService.setFnolFlow(this.policy);
                        this.isAuto = this.policyType === 'Business Auto';
                    }
                    this.celebrusService.storeProductType();
                    this.retrieveOpenClaims();
                    this.iconSvgService.init();
                    this.claimsDataService.setFromSearch(false);
                } else {
                    this.package = {};
                    this.commercialLoggingService.logServiceFailure(
                        this.LOGGER,
                        this.currentPage,
                        'PackageService',
                        'retrievePackageDataforClaims',
                        this.getLogMetadata(),
                        { code: 'NO_PACKAGE_DATA' }
                    );
                }
            } catch (error) {
                this.package = {};
                this.commercialLoggingService.logServiceFailure(
                    this.LOGGER,
                    this.currentPage,
                    'PolicyClaimsListComponent',
                    'mapPackageData',
                    this.getLogMetadata(),
                    error
                );
            }
        }, (error: any) => {
            this.package = {};
            this.commercialLoggingService.logServiceFailure(
                this.LOGGER,
                this.currentPage,
                'PackageService',
                'retrievePackageDataforClaims',
                this.getLogMetadata(),
                error
            );
        });
    }

    onClosedClaimFilterChanged(value: boolean): void {
        this.closedClaimsFilter = value;
        this.isLoadComplete = false;
        if (this.closedClaimsFilter) {
            this.checkClosedClaims(this.closedClaimsFilter);
        } else {
            this.retrieveOpenClaims();
        }
    }
    onPageChange(eventOffset: any): void {
        this.previousDisplayOffset = this.displayOffset;
        this.displayOffset = eventOffset;
        const element = document.querySelector('#claimsHeading');
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }

    processClaimsList(claimSummaries: any): void {
        this.claimsList = [];
        for (const claimSummary of claimSummaries) {
            if (this.formatPolicyNumberService.policyNumbersMatch(claimSummary.policyNumber, this.policyNumber)) {
                this.claimsList.push(claimSummary);
            }
        }
        if (this.claimsList.length > 0) {
            this.hasClaims = true;
            this.totalClaims = this.claimsList.length;
        }
        this.isLoadComplete = true;
    }

    retrieveOpenClaims(): void {
        this.searchClaimsService.retrieveOpenClosedClaims(false, this.claimsDataService.getClaimsAccountNumber()).then((response) => {
            const numberOfClaims = response.claimSummaries.length;
            if (numberOfClaims > 0) {
                this.hasOpenClaims = true;
                this.processClaimsList(response.claimSummaries);
            } else {
                this.checkClosedClaims(true);
            }
        });
    }

    showClaims(): boolean {
        if (this.hasOpenClaims) {
            return true;
        } else if (this.closedClaimsFilter) {
            return true;
        } else {
            return false;
        }
    }

    startClaim(): void {
        this.claimsDataService.setClaimsPolicyNumber(this.policyNumber);
        this.celebrusService.storeProductType();
        this.claimsDataService.setClaimsPolicyType(this.policyType);
        this.claimsDataService.setFnolFlow(this.policy);
        this.ctmLogConfirmationEvent();
        this.router.navigate([commercialConstants.URLS.firstNoticeOfLoss]);
    }

    private getLogMetadata(additionalFields: any = {}): any {
        return {
            policyNumber: this.claimsDataService.getClaimsPolicyNumber(),
            policyType: this.claimsDataService.getClaimsPolicyType(),
            lineOfBusiness: this.claimsDataService.getFnolFlow(),
            claimsDisplayCount: this.claimsDataService.getClaimsDisplayCount() || this.totalClaims || '0',
            alphaState: this.claimsDataService.getPrimary(this.claimsDataService.getClaimsPackage()?.address)?.state || 'UNKNOWN',
            previousUrl: window.sessionStorage.getItem('previousUrl') || '',
            ...additionalFields
        };
    }
}
